import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * ConfirmModal - window.confirm() ka modern replacement
 *
 * Props:
 *   isOpen      - boolean
 *   title       - string
 *   message     - string
 *   confirmText - string (default: "Confirm")
 *   cancelText  - string (default: "Cancel")
 *   danger      - boolean — red styling for destructive actions
 *   onConfirm   - function
 *   onCancel    - function
 */
export default function ConfirmModal({
  isOpen,
  title = 'Are you sure?',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-scale-in">
        {/* Header stripe */}
        <div className={`h-1.5 w-full ${danger ? 'bg-gradient-to-r from-red-500 to-rose-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`} />

        <div className="p-6">
          {/* Icon + Title */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${danger ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-gray-900">{title}</h3>
              {message && (
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{message}</p>
              )}
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-bold text-white transition-all active:scale-95 shadow-md
                ${danger
                  ? 'bg-red-600 hover:bg-red-700 shadow-red-200'
                  : 'bg-amber-500 hover:bg-amber-600 shadow-amber-200'
                }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
